import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, Minus, X } from 'lucide-react';
import { useBetaFeedbackWidget } from '../hooks/useBetaFeedbackWidget';
import { useAuth } from '../hooks/useAuth';
import { getZIndexClass } from '../config/zIndex';

interface BetaFeedbackWidgetProps {
  onFeedbackClick?: () => void;
  className?: string;
}

/**
 * Simple beta feedback widget pill
 * MVP implementation based on George's recommendations
 */
export function BetaFeedbackWidget({ onFeedbackClick, className = '' }: BetaFeedbackWidgetProps) {
  const { isVisible, dismissWeek } = useBetaFeedbackWidget();
  const [isMinimized, setIsMinimized] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Track impressions - critical for measuring reach
  useEffect(() => {
    if (isVisible) {
      // Use existing analytics pattern if available
      interface WindowWithAnalytics extends Window {
        analytics?: {
          track: (event: string, properties: Record<string, unknown>) => void;
        };
      }
      const windowWithAnalytics = window as WindowWithAnalytics;
      
      if (typeof window !== 'undefined' && windowWithAnalytics.analytics?.track) {
        windowWithAnalytics.analytics.track('beta_feedback_widget_shown', {
          page: location.pathname,
          user_id: user?.id
        });
      }
      
      // Fallback to console in development
      if (import.meta.env.DEV) {
        console.log('Beta Feedback Widget shown:', {
          page: location.pathname,
          user_id: user?.id
        });
      }
    }
  }, [isVisible, location.pathname, user?.id]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`fixed ${getZIndexClass('overlay')} animate-in fade-in slide-in-from-bottom-2 duration-300 
                     ${className}`}
         style={{
           // Position above bottom nav (64px) + chat input (~60px) + spacing (16px) = ~140px from bottom
           bottom: location.pathname.includes('/chat') ? '9rem' : '5rem',
           right: '1.5rem',
           paddingBottom: 'env(safe-area-inset-bottom, 0)'
         }}>
      {isMinimized ? (
        // Minimized state: Just the icon
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-oro-gold text-oro-bg p-3 rounded-full shadow-floating hover:scale-105 transition-all duration-180 focus:outline-none focus:ring-2 focus:ring-oro-gold focus:ring-offset-2 focus:ring-offset-oro-bg"
          aria-label="Expand beta feedback"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      ) : (
        // Expanded state: Full pill with controls
        <div className="bg-oro-surface border border-oro-border rounded-full shadow-floating flex items-center gap-3 px-4 py-3 min-h-11">
          {/* Main feedback button */}
          <button
            onClick={onFeedbackClick}
            className="flex items-center gap-2 text-text-primary hover:text-oro-gold transition-colors duration-180 focus:outline-none focus:text-oro-gold"
          >
            <HelpCircle className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium whitespace-nowrap">Beta Feedback</span>
          </button>
          
          {/* Minimize button */}
          <button
            onClick={() => setIsMinimized(true)}
            className="text-text-muted hover:text-text-primary transition-colors duration-180 p-1 rounded focus:outline-none focus:text-text-primary min-w-6 min-h-6 flex items-center justify-center"
            aria-label="Minimize feedback widget"
          >
            <Minus className="w-4 h-4" />
          </button>
          
          {/* Dismiss button */}
          <button
            onClick={dismissWeek}
            className="text-text-muted hover:text-text-primary transition-colors duration-180 p-1 rounded focus:outline-none focus:text-text-primary min-w-6 min-h-6 flex items-center justify-center"
            aria-label="Dismiss feedback widget for 1 week"
            title="Remind me in 7 days"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}